/**
 * API 请求封装
 * 基于 Axios，统一处理请求/响应/错误
 */
import axios from 'axios'
import { Message } from 'element-ui'
import router from '../router'

// 创建 axios 实例
const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器：自动附加 Token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('petboss_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器：统一处理错误
api.interceptors.response.use(
  response => {
    // blob 响应（文件下载）直接返回原始数据
    if (response.config.responseType === 'blob') {
      return response.data
    }
    const res = response.data
    console.log('API Response:', res)
    if (res.Result === 0 || res.code === 'SUCCESS') {
      const data = res.Data !== undefined ? res.Data : res.data !== undefined ? res.data : res
      console.log('Extracted Data:', data)
      return data
    }
    // 业务错误
    const errorMessage = res.Message || res.message || '请求失败'
    console.error('API Error:', errorMessage)
    Message.error(errorMessage)
    return Promise.reject(new Error(errorMessage))
  },
  error => {
    const status = error.response ? error.response.status : 0
    const data = error.response ? error.response.data : {}
    console.error('HTTP Error:', status, data)

    // 如果当前已经在登录页，不弹错误（退出登录过程中的残留请求）
    if (router.currentRoute.path === '/login') return Promise.reject(error)

    if (status === 401) {
      Message.error('登录已过期，请重新登录')
      localStorage.removeItem('petboss_token')
      localStorage.removeItem('petboss_user')
      router.push('/login')
    } else if (status === 403) {
      Message.error('没有权限执行此操作')
    } else if (status === 404) {
      Message.error('接口不存在')
    } else if (status >= 500) {
      Message.error(data.message || '服务器错误')
    } else {
      Message.error(error.message || '网络错误')
    }

    return Promise.reject(error)
  }
)

// ==================== 认证 API ====================
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.put('/auth/password', data)
}

// ==================== 会员 API ====================
export const memberAPI = {
  getList: (params) => api.get('/members', { params }),
  getDetail: (id) => api.get(`/members/${id}`),
  create: (data) => api.post('/members', data),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id) => api.delete(`/members/${id}`),
  search: (keyword) => api.get('/members/search', { params: { keyword } })
}

// ==================== 订单 API ====================
export const orderAPI = {
  getList: (params) => api.get('/orders', { params }),
  getDetail: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  cancel: (id) => api.delete(`/orders/${id}`)
}

// ==================== 商品 API ====================
export const productAPI = {
  getList: (params) => api.get('/products', { params }),
  getDetail: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  search: (keyword) => api.get('/products/search', { params: { keyword } }),
  getCategoryTree: () => api.get('/products/categories/tree')
}

// ==================== 库存 API ====================
export const stockAPI = {
  getList: (params) => api.get('/stock/list', { params }),
  stockIn: (data) => api.post('/stock/in', data),
  stockOut: (data) => api.post('/stock/out', data),
  getLogs: (params) => api.get('/stock/logs', { params })
}

// ==================== 服务 API ====================
export const serviceAPI = {
  getList: (params) => api.get('/services', { params }),
  getDetail: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  getCategoryTree: () => api.get('/services/categories/tree')
}

// ==================== 活体 API ====================
export const livingAPI = {
  getList: (params) => api.get('/living', { params }),
  getDetail: (id) => api.get(`/living/${id}`),
  create: (data) => api.post('/living', data),
  update: (id, data) => api.put(`/living/${id}`, data),
  delete: (id) => api.delete(`/living/${id}`),
  getCategoryTree: () => api.get('/living/categories/tree')
}

// ==================== 套餐 API ====================
export const packageAPI = {
  getList: (params) => api.get('/packages', { params }),
  getDetail: (id) => api.get(`/packages/${id}`),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`)
}

// ==================== 寄养 API ====================
export const boardingAPI = {
  getList: (params) => api.get('/boarding', { params }),
  getDetail: (id) => api.get(`/boarding/${id}`),
  create: (data) => api.post('/boarding', data),
  update: (id, data) => api.put(`/boarding/${id}`, data),
  delete: (id) => api.delete(`/boarding/${id}`),
  getActive: () => api.get('/boarding/active')
}

// ==================== 宠物 API ====================
export const petAPI = {
  getList: (params) => api.get('/pets', { params }),
  getDetail: (id) => api.get(`/pets/${id}`),
  create: (data) => api.post('/pets', data),
  update: (id, data) => api.put(`/pets/${id}`, data),
  delete: (id) => api.delete(`/pets/${id}`)
}

// ==================== 预约 API ====================
export const appointmentAPI = {
  getList: (params) => api.get('/appointments', { params }),
  getDetail: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  getTodayPending: () => api.get('/appointments/today-pending')
}

// ==================== 设备 API ====================
export const deviceAPI = {
  getList: (params) => api.get('/devices', { params }),
  getDetail: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  delete: (id) => api.delete(`/devices/${id}`),
  reportStatus: (id, data) => api.post(`/devices/${id}/status`, data)
}

// ==================== 设备订单 API ====================
export const deviceOrderAPI = {
  getList: (params) => api.get('/device-orders', { params }),
  getDetail: (id) => api.get(`/device-orders/${id}`),
  create: (data) => api.post('/device-orders', data),
  pay: (id, data) => api.put(`/device-orders/${id}/pay`, data),
  start: (id) => api.put(`/device-orders/${id}/start`),
  complete: (id) => api.put(`/device-orders/${id}/complete`),
  cancel: (id) => api.put(`/device-orders/${id}/cancel`)
}

// ==================== 店铺概况 API ====================
export const surveyAPI = {
  getOverview: (shopId) => api.get('/survey/overview', { params: { shopId } }),
  getTraffic: (params) => api.get('/survey/traffic', { params }),
  getShopInfo: (shopId) => api.get('/survey/shop-info', { params: { shopId } }),
  getShops: () => api.get('/survey/shops')
}

// ==================== 洗护报告 API ====================
export const groomingAPI = {
  getList: (params) => api.get('/grooming', { params }),
  getDetail: (id) => api.get(`/grooming/${id}`),
  create: (data) => api.post('/grooming', data),
  update: (id, data) => api.put(`/grooming/${id}`, data),
  delete: (id) => api.delete(`/grooming/${id}`)
}

// ==================== 提醒 API ====================
export const remindAPI = {
  getList: (params) => api.get('/remind', { params }),
  getDetail: (id) => api.get(`/remind/${id}`),
  create: (data) => api.post('/remind', data),
  update: (id, data) => api.put(`/remind/${id}`, data),
  delete: (id) => api.delete(`/remind/${id}`)
}

// ==================== 检查项目模板 API ====================
export const groomingTemplateAPI = {
  getList: () => api.get('/grooming-templates'),
  getDetail: (id) => api.get(`/grooming-templates/${id}`),
  create: (data) => api.post('/grooming-templates', data),
  update: (id, data) => api.put(`/grooming-templates/${id}`, data),
  delete: (id) => api.delete(`/grooming-templates/${id}`)
}

// ==================== 记账本 API ====================
export const accountBookAPI = {
  getList: (params) => api.get('/account-book', { params }),
  getDetail: (id) => api.get(`/account-book/${id}`),
  create: (data) => api.post('/account-book', data),
  update: (id, data) => api.put(`/account-book/${id}`, data),
  delete: (id) => api.delete(`/account-book/${id}`)
}

// ==================== 会员卡模板 API ====================
export const memberCardTemplateAPI = {
  getList: () => api.get('/member-card-templates'),
  getDetail: (id) => api.get(`/member-card-templates/${id}`),
  create: (data) => api.post('/member-card-templates', data),
  update: (id, data) => api.put(`/member-card-templates/${id}`, data),
  delete: (id) => api.delete(`/member-card-templates/${id}`)
}

// ==================== 次卡模板 API ====================
export const timeCardTemplateAPI = {
  getList: (params) => api.get('/time-card-templates', { params }),
  getDetail: (id) => api.get(`/time-card-templates/${id}`),
  create: (data) => api.post('/time-card-templates', data),
  update: (id, data) => api.put(`/time-card-templates/${id}`, data),
  delete: (id) => api.delete(`/time-card-templates/${id}`)
}

// ==================== 积分规则 API ====================
export const pointTemplateAPI = {
  getList: () => api.get('/point-templates'),
  getDetail: (id) => api.get(`/point-templates/${id}`),
  create: (data) => api.post('/point-templates', data),
  update: (id, data) => api.put(`/point-templates/${id}`, data),
  delete: (id) => api.delete(`/point-templates/${id}`)
}

// ==================== 品牌 API ====================
export const brandAPI = {
  getList: (params) => api.get('/brands', { params }),
  getDetail: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`)
}

// ==================== 供应商 API ====================
export const supplierAPI = {
  getList: (params) => api.get('/suppliers', { params }),
  getDetail: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`)
}

// ==================== 会员营销 API ====================
export const memberMarketingAPI = {
  getList: (params) => api.get('/member-marketing', { params }),
  sendSms: (data) => api.post('/member-marketing/send-sms', data),
  batchSendSms: (data) => api.post('/member-marketing/batch-send-sms', data),
  sendCoupon: (data) => api.post('/member-marketing/send-coupon', data),
  batchSendCoupon: (data) => api.post('/member-marketing/batch-send-coupon', data)
}

// ==================== 店铺管理 API ====================
export const shopAPI = {
  getList: (params) => api.get('/shops', { params }),
  getDetail: (id) => api.get(`/shops/${id}`),
  create: (data) => api.post('/shops', data),
  update: (id, data) => api.put(`/shops/${id}`, data),
  delete: (id) => api.delete(`/shops/${id}`)
}

// ==================== 加盟商管理 API ====================
export const franchiseAPI = {
  getList: (params) => api.get('/franchises', { params }),
  create: (data) => api.post('/franchises', data),
  update: (id, data) => api.put(`/franchises/${id}`, data),
  delete: (id) => api.delete(`/franchises/${id}`)
}

// ==================== 排班管理 API ====================
export const scheduleAPI = {
  getList: (params) => api.get('/schedules/list', { params }),
  update: (data) => api.post('/schedules/update', data),
  export: (params) => api.get('/schedules/export', { params, responseType: 'blob' }),
  import: (data) => api.post('/schedules/import', data)
}

// ==================== 店员管理 API ====================
export const staffAPI = {
  getList: (params) => api.get('/users/list', { params }),
  getRoles: () => api.get('/users/roles'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.put(`/users/${id}/reset-password`),
  createRole: (data) => api.post('/users/roles', data),
  updateRole: (id, data) => api.put(`/users/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/users/roles/${id}`)
}

export default api
